// ReplayControls - production-visible replay mode chrome (TD043 Phase 1).
//
// Replay is a mode over the user's whole dashboard, not a widget: entering it
// drives every panel through the shared stores playbackStore.applyCutoff
// writes into (optionsStore / GreeksWebsocketDataStore /
// WebSocketDataStore), so the user's own layout replays in place with no
// dedicated review surface. The auto-buffer runs continuously from mount,
// independent of the toggle, so toggling on never waits for a first capture
// and is never paused by an auto-entering effect (see TD043 defect #1).
//
// This toggle only ever calls enterBufferReview or exitPlayback.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import usePlaybackStore from '../../Store/playbackStore';
import useDashboardConfig from '../../Store/dashboardConfigStore';
import { PARTIAL_REPLAY_WIDGET_IDS } from './widgetRegistry';
import { etDateKey, sessionOpenEpochMs } from '../../Utils/marketHours';
import {
  buildMinuteMarkers,
  replayCoverageNeedsRecovery,
  replayExpectedEndT,
} from '../../Utils/replayGaps';
import '../../Styles/replayControls.css';

const fmtClock = (t) => {
  try {
    return new Date(t).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York',
    });
  } catch { return '--:--'; }
};

// The scrub box travels with the thumb, so its width is a layout constraint.
// The visible range labels stay as bare clocks because the chart and Bookmap
// already show the replay date.
const fmtScrubClock = (t) => `${fmtClock(t)} ET`;
const REPLAY_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const fmtReplayBannerDate = (tradingDate, fallbackT) => {
  const dateKey = /^\d{4}-\d{2}-\d{2}$/.test(tradingDate || '')
    ? tradingDate
    : (Number.isFinite(fallbackT) ? etDateKey(fallbackT) : null);
  if (!dateKey) return '---';
  const [, month, day] = dateKey.split('-');
  return `${REPLAY_MONTHS[Number(month) - 1]}-${day}`;
};

const TOUCH_HOVER_SUPPRESSION_MS = 750;

function replaySourcesNeedRecovery(coverage) {
  const entries = Object.values(coverage || {});
  return entries.length === 0 || entries.some(
    (entry) => !['complete', 'notRequested'].includes(entry?.status),
  );
}

// The date remains in aria-valuetext so screen-reader announcements keep their
// established session context.
const fmtReplayDate = (t) => {
  if (etDateKey(t) === etDateKey(Date.now())) return null;
  return new Date(t).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'America/New_York',
  });
};

const fmtReplayTime = (t) => {
  const date = fmtReplayDate(t);
  return date ? `${date}, ${fmtScrubClock(t)}` : fmtScrubClock(t);
};

function fmtHoverReadout(marker) {
  if (!marker) return '--:--';
  return fmtScrubClock(marker.t);
}

function markerIndexFromPointer(event, markerCount, thumbWidth) {
  if (markerCount < 2) return 0;
  const bounds = event.currentTarget.getBoundingClientRect();
  const trackStart = bounds.left + thumbWidth / 2;
  const trackWidth = bounds.width - thumbWidth;
  if (trackWidth <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, (event.clientX - trackStart) / trackWidth));
  return Math.round(ratio * (markerCount - 1));
}

// Nearest marker AT OR BEFORE the frame, not an exact match: a Recorder fixture
// can be stepped (play/pause) to a frame that is not itself a marker, and an
// exact findIndex returns -1 there, snapping the slider to position 0 and
// misreporting the user's position as the start of the session.
function markerIndexAtOrBefore(markers, frameIndex, replayGap) {
  if (replayGap && Number.isFinite(replayGap.t)) {
    const gapIndex = markers.findIndex((marker) => (
      marker.kind === 'gap' && Math.floor(marker.t / 60_000) === Math.floor(replayGap.t / 60_000)
    ));
    if (gapIndex >= 0) return gapIndex;
  }
  let result = 0;
  for (let i = 0; i < markers.length; i++) {
    if (markers[i].kind === 'frame' && markers[i].frameIndex <= frameIndex) result = i;
  }
  return result;
}

export default function ReplayControls({
  controlTabIndex,
  onRecoverReplayHistory,
  replayDates = [],
  replayDateDiscoveryReady = true,
  onReplayRequest,
  onReplayRequestSettled,
  onReplayExit,
}) {
  const mode = usePlaybackStore((s) => s.mode);
  const frames = usePlaybackStore((s) => s.frames);
  const index = usePlaybackStore((s) => s.index);
  const replayGap = usePlaybackStore((s) => s.replayGap);
  const replayCoverage = usePlaybackStore((s) => s.replayCoverage);
  const replayDatasetSession = usePlaybackStore((s) => s.replayDatasetSession);
  const replayDateSelection = usePlaybackStore((s) => s.replayDateSelection);
  const stagedReplayDate = usePlaybackStore((s) => s.stagedReplayDate);
  const stagedReplayFrameCount = usePlaybackStore((s) => s.stagedReplayFrames.length);
  const selectedReplayTradingDate = useDashboardConfig((s) => s.replayTradingDate);
  const selectedReplayDateDiscovery = useDashboardConfig(
    (s) => s.replayTradingDateDiscovery,
  );
  const latestReplayTradingDate = useDashboardConfig((s) => s.latestReplayTradingDate);
  const replayHistoryActive = usePlaybackStore((s) => s.replayHistoryActive);
  const bufferFrames = usePlaybackStore((s) => s.bufferFrames);
  const bufferDate = usePlaybackStore((s) => s.bufferDate);
  const bufferFrameCount = bufferFrames.length;
  const availableReplayFrameCount = replayDateSelection?.tradingDate
    ? stagedReplayFrameCount
    : bufferFrameCount;
  const historyHydrated = usePlaybackStore((s) => s.historyHydrated);
  const historyHydrating = usePlaybackStore((s) => s.historyHydrating);
  const historyIntentChecking = usePlaybackStore((s) => s.historyIntentChecking);
  const historyError = usePlaybackStore((s) => s.historyError);
  const replaying = mode === 'playing';
  const [enterFailed, setEnterFailed] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [hoveredMarkerIndex, setHoveredMarkerIndex] = useState(null);
  const touchHoverUntilRef = useRef(0);
  const recoveryRequestRef = useRef(0);
  const scrubThumbMeasureRef = useRef(null);
  const scrubThumbWidthRef = useRef(0);
  const scrubReadoutRef = useRef(null);
  const [scrubReadoutHalfWidth, setScrubReadoutHalfWidth] = useState(32);
  const [startupChecking, setStartupChecking] = useState(
    () => !usePlaybackStore.getState().historyHydrated,
  );
  const replayDateDiscovery = useMemo(
    () => replayDates.find((entry) => entry.tradingDate === selectedReplayTradingDate) || null,
    [replayDates, selectedReplayTradingDate],
  );
  const latestDiscoveredReplayDate = useMemo(
    () => replayDates.find((entry) => entry.isLatest)?.tradingDate || null,
    [replayDates],
  );

  useEffect(() => {
    let mounted = true;
    usePlaybackStore.getState().startAutoBuffer();
    const hydrateOnStartup = async () => {
      const store = usePlaybackStore.getState();
      if (
        store.replayDateSelection?.tradingDate === selectedReplayTradingDate
        && store.stagedReplayFrames.length
      ) {
        return;
      }
      const restored = await store.restoreReplayIfActive({
        partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
        tradingDate: selectedReplayTradingDate,
        discovery: replayDateDiscovery,
      });
      if (!restored) {
        const current = usePlaybackStore.getState();
        if (
          selectedReplayTradingDate
          && current.replayDateSelection?.tradingDate !== selectedReplayTradingDate
        ) {
          await current.selectReplayHistoryDate(selectedReplayTradingDate, {
            partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
            discovery: replayDateDiscovery,
          });
        } else {
          await current.hydrateReplayHistory({
            partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
            tradingDate: selectedReplayTradingDate,
            discovery: replayDateDiscovery,
            loadDatasetRecords: Boolean(selectedReplayTradingDate),
          });
        }
      }
    };
    void hydrateOnStartup()
      .catch((err) => {
        console.error('[ReplayControls] startup replay hydration failed:', err);
      })
      .finally(() => {
        if (mounted) setStartupChecking(false);
      });
    return () => { mounted = false; };
  }, [selectedReplayTradingDate, replayDateDiscovery]);

  useEffect(() => {
    setEnterFailed(false);
  }, [bufferFrames]);

  useEffect(() => {
    if (!replaying) setHoveredMarkerIndex(null);
  }, [replaying]);

  const recoveryStatus = recoveryResult?.status || null;
  const hasNormalizedCoverage = Boolean(replayCoverage);
  const showCoverageBounds = replayHistoryActive && (
    recovering || (recoveryStatus && recoveryStatus !== 'complete')
  );
  const markers = useMemo(() => {
    if (!replaying) return [];
    const built = buildMinuteMarkers(frames, {
      startT: showCoverageBounds && frames[0]
        ? sessionOpenEpochMs(frames[0].t)
        : null,
      endT: showCoverageBounds
        ? replayExpectedEndT(frames)
        : null,
    });
    return hasNormalizedCoverage
      ? built.map((marker) => (marker.kind === 'frame' ? { ...marker, partial: false } : marker))
      : built;
  }, [frames, replaying, showCoverageBounds, hasNormalizedCoverage]);
  const markerIndex = markerIndexAtOrBefore(markers, index, replayGap);
  const cur = markers[markerIndex];
  const previewing = Number.isInteger(hoveredMarkerIndex) && markers[hoveredMarkerIndex];
  const readoutMarkerIndex = previewing ? hoveredMarkerIndex : markerIndex;
  const readoutMarker = markers[readoutMarkerIndex];
  const firstAvailableFrame = frames[0];
  const lastAvailableFrame = frames[frames.length - 1];
  const scrubReadoutText = previewing
    ? fmtHoverReadout(readoutMarker)
    : (cur ? fmtScrubClock(cur.t) : '--:--');
  // Drives the selected-minute box's horizontal position in CSS. The track is
  // inset by half a thumb on each side, so the ratio maps onto
  // (100% - thumbWidth) rather than the full shell width.
  const scrubRatio = markers.length > 1 ? readoutMarkerIndex / (markers.length - 1) : 0;
  const bufferedDay = bufferFrames.length
    ? etDateKey(bufferFrames[bufferFrames.length - 1].t)
    : null;
  const bufferedDate = bufferedDay && bufferedDay !== etDateKey(Date.now())
    ? new Date(bufferFrames[bufferFrames.length - 1].t).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'America/New_York',
      })
    : null;
  const replayTradingDate = replayDatasetSession?.tradingDate || (
    firstAvailableFrame ? etDateKey(firstAvailableFrame.t) : null
  );
  const replayDateIsNotLatest = Boolean(
    replayTradingDate && latestReplayTradingDate && replayTradingDate !== latestReplayTradingDate,
  );
  const replayTargetDate = replayDateSelection?.tradingDate
    || stagedReplayDate
    || selectedReplayTradingDate
    || (bufferFrameCount ? bufferedDay : null)
    || bufferDate;
  const replayTargetLabel = replayDateSelection?.tradingDate
    || stagedReplayDate
    || selectedReplayTradingDate
    || (bufferFrameCount ? (bufferedDate || bufferedDay) : null)
    || bufferDate;
  const replayTargetDiscovery = replayDates.find(
    (entry) => entry?.tradingDate === replayTargetDate,
  ) || (
    selectedReplayDateDiscovery?.tradingDate === replayTargetDate
      ? selectedReplayDateDiscovery
      : null
  );
  const replayTargetIsNotLatest = replayTargetDiscovery
    ? replayTargetDiscovery.isLatest === false
    : Boolean(
        replayTargetDate
        && (latestDiscoveredReplayDate || latestReplayTradingDate)
        && replayTargetDate !== (latestDiscoveredReplayDate || latestReplayTradingDate),
      );
  const loadingHistory = startupChecking || historyIntentChecking || historyHydrating
    || (!replaying && recovering) || !replayDateDiscoveryReady;

  useLayoutEffect(() => {
    const width = scrubReadoutRef.current?.getBoundingClientRect().width;
    if (!Number.isFinite(width) || width <= 0) return;
    const halfWidth = width / 2;
    setScrubReadoutHalfWidth((currentWidth) => (
      currentWidth === halfWidth ? currentWidth : halfWidth
    ));
  }, [scrubReadoutText]);

  const onToggle = async (event) => {
    if (replaying) {
      // Release focus on every exit path, keyboard included: a focused toggle
      // sits inside the TopBar and would hold the bar open after replay ends.
      event.currentTarget.blur();
      recoveryRequestRef.current += 1;
      setRecovering(false);
      setRecoveryResult(null);
      usePlaybackStore.getState().exitPlayback();
      onReplayExit?.();
      setEnterFailed(false);
      return;
    }
    onReplayRequest?.();
    recoveryRequestRef.current += 1;
    setRecoveryResult(null);
    let requestSettled = false;
    const settleRequest = () => {
      if (requestSettled) return;
      requestSettled = true;
      onReplayRequestSettled?.();
    };
    const recoverHistory = async (requestedDay) => {
      const recoveryRequest = ++recoveryRequestRef.current;
      setRecovering(true);
      try {
        const recovery = await onRecoverReplayHistory(requestedDay);
        if (recoveryRequest === recoveryRequestRef.current) setRecoveryResult(recovery || null);
        return recovery;
      } catch (err) {
        console.error('[ReplayControls] replay recovery failed:', err);
        if (recoveryRequest === recoveryRequestRef.current) {
          setRecoveryResult({ status: 'fetch_failed' });
        }
        return { status: 'fetch_failed' };
      } finally {
        if (recoveryRequest === recoveryRequestRef.current) setRecovering(false);
      }
    };
    try {
      const staged = usePlaybackStore.getState();
      if (
        staged.replayDateSelection?.tradingDate === selectedReplayTradingDate
        && staged.stagedReplayFrames.length
      ) {
        const entered = staged.activateReplayHistoryDate({
          partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
        });
        setEnterFailed(!entered);
        if (entered) {
          settleRequest();
          return;
        }
      }
      if (!historyHydrated) {
        await usePlaybackStore.getState().hydrateReplayHistory({
          partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
          tradingDate: selectedReplayTradingDate,
          discovery: replayDateDiscovery,
        });
        if (usePlaybackStore.getState().mode === 'playing') {
          setEnterFailed(false);
          settleRequest();
          return;
        }
      }
      const selectedStage = usePlaybackStore.getState();
      if (selectedStage.replayDateSelection?.tradingDate === selectedReplayTradingDate) {
        if (!selectedStage.stagedReplayFrames.length && onRecoverReplayHistory) {
          await recoverHistory(selectedReplayTradingDate);
        }
        const recoveredStage = usePlaybackStore.getState();
        const entered = recoveredStage.replayDateSelection?.tradingDate
          === selectedReplayTradingDate
          && recoveredStage.stagedReplayFrames.length > 0
          && recoveredStage.activateReplayHistoryDate({
            partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
          });
        setEnterFailed(!entered);
        return;
      }
      const playback = usePlaybackStore.getState();
      const needsRecovery = onRecoverReplayHistory
        && (
          replayCoverageNeedsRecovery(playback.bufferFrames)
          || replaySourcesNeedRecovery(playback.replayCoverage)
        );
      const requestedDay = selectedReplayTradingDate || playback.bufferDate;
      if (playback.bufferFrames.length) {
        const entered = usePlaybackStore.getState().enterBufferReview({
          partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
        });
        setEnterFailed(!entered);
        if (entered) {
          settleRequest();
          if (needsRecovery) void recoverHistory(requestedDay);
          return;
        }
      }
      if (needsRecovery) await recoverHistory(requestedDay);
      // enterBufferReview returns false when the buffer fails validation (one
      // malformed frame fails frames.every(isValidFrame)). Without surfacing it
      // the button is an enabled dead click.
      setEnterFailed(!usePlaybackStore.getState().enterBufferReview({
        partialWidgetIds: PARTIAL_REPLAY_WIDGET_IDS,
      }));
    } catch (err) {
      console.error('[ReplayControls] replay entry failed:', err);
      setEnterFailed(true);
    } finally {
      // Drop the hydrate hold even when entry fails or hydrate throws. Leaving
      // it set would pin the bar with leave and tap-outside both dead.
      settleRequest();
    }
  };

  const title = replaying
    ? 'Exit replay and return to live data'
    : loadingHistory
      ? 'Loading replay history'
      : historyHydrated && availableReplayFrameCount === 0
      ? onRecoverReplayHistory
        ? 'Check the server for replay history'
        : "No replay data yet -- check back once the market's been open a few minutes"
      : enterFailed
        ? 'Replay could not start -- the buffered session failed validation'
        : replayTargetLabel
          ? `Replay ${replayTargetLabel} session`
          : "Replay today's session so far";

  return (
    <div className={`replay-chrome${replaying ? ' replay-chrome--active' : ''}${replaying && replayDateIsNotLatest ? ' replay-chrome--historical' : ''}`}>
      <button
        type="button"
        className={`replay-toggle ${replayTargetIsNotLatest ? 'replay-toggle--historical' : 'replay-toggle--latest'}`}
        tabIndex={controlTabIndex}
        onClick={(event) => { void onToggle(event); }}
        disabled={!replaying && (
          loadingHistory
          || (historyHydrated && availableReplayFrameCount === 0 && !onRecoverReplayHistory)
        )}
        aria-pressed={replaying}
        title={title}
      >
        {replaying
          ? 'Exit Replay'
          : loadingHistory
            ? 'Loading...'
            : 'Replay'}
      </button>

      {/* title is a no-op on touch (no hover/long-press affordance), so the
          disabled reason needs to be visible, not just attached as a tooltip. */}
      {!replaying && bufferFrameCount > 0 && enterFailed && (
        <span className="replay-hint">Replay unavailable</span>
      )}
      {!replaying && !replayDateDiscoveryReady && (
        <span className="replay-hint" role="status">Loading historical data...</span>
      )}
      {historyError && replayDateDiscoveryReady && (
        <span className="replay-hint" role="alert">Replay history unavailable; current session only</span>
      )}

      {replaying && (
        <div className="replay-banner" role="status">
          <span className="replay-banner__identity">
            <span className="replay-banner__label">REPLAY</span>
            <span
              className={`replay-banner__date${replayDateIsNotLatest ? ' replay-banner__date--not-latest' : ''}`}
              aria-label={replayDateIsNotLatest
                ? `Selected replay date ${replayTradingDate}; this is not the latest trading date.`
                : undefined}
            >
              {fmtReplayBannerDate(replayTradingDate, firstAvailableFrame?.t)}
            </span>
          </span>
          <span className="replay-clock replay-clock--start" title="First available replay minute">
            {firstAvailableFrame ? fmtScrubClock(firstAvailableFrame.t) : '--:--'}
          </span>
          <div
            className={`replay-scrub-shell${previewing ? ' replay-scrub-shell--preview' : ''}`}
            style={{
              '--replay-scrub-ratio': scrubRatio,
              '--replay-scrub-readout-half-width': `${scrubReadoutHalfWidth}px`,
            }}
          >
            <span className="replay-scrub-thumb-measure" ref={scrubThumbMeasureRef} aria-hidden="true" />
            {/* The preview only changes this visual readout. The slider keeps
                its selected marker and aria-valuetext until a seek occurs. */}
            <span
              ref={scrubReadoutRef}
              className="replay-scrub-selected"
              aria-hidden="true"
            >
              {scrubReadoutText}
            </span>
            <input
              className="replay-scrub"
              type="range"
              tabIndex={controlTabIndex}
              min={0}
              max={Math.max(0, markers.length - 1)}
              value={markerIndex}
              aria-label="Replay position"
              aria-valuetext={cur ? fmtReplayTime(cur.t) : undefined}
              onChange={(e) => {
                const marker = markers[Number(e.target.value)];
                if (marker) usePlaybackStore.getState().seekMarker(marker);
              }}
              onMouseMove={(event) => {
                if (
                  Date.now() < touchHoverUntilRef.current
                  || event.nativeEvent.sourceCapabilities?.firesTouchEvents
                ) return;
                const nextIndex = markerIndexFromPointer(
                  event,
                  markers.length,
                  scrubThumbWidthRef.current,
                );
                setHoveredMarkerIndex((currentIndex) => (
                  currentIndex === nextIndex ? currentIndex : nextIndex
                ));
              }}
              onMouseEnter={() => {
                // Measured on every enter, synthetic touch ones included: the
                // touch guard belongs on the preview state, and skipping the
                // measurement here leaves the pointer mapping on a 0 thumb.
                const width = scrubThumbMeasureRef.current?.getBoundingClientRect().width;
                scrubThumbWidthRef.current = Number.isFinite(width) ? width : 0;
              }}
              onPointerDown={(event) => {
                if (event.pointerType !== 'touch') return;
                touchHoverUntilRef.current = Date.now() + TOUCH_HOVER_SUPPRESSION_MS;
                setHoveredMarkerIndex(null);
              }}
              onKeyDown={() => { setHoveredMarkerIndex(null); }}
              onMouseLeave={() => { setHoveredMarkerIndex(null); }}
            />
          </div>
          <span className="replay-clock replay-clock--end" title="Last available replay minute">
            {lastAvailableFrame ? fmtScrubClock(lastAvailableFrame.t) : '--:--'}
          </span>
        </div>
      )}
    </div>
  );
}
